import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import './TextViewer.scss';
import {highlightElement, scrollToElementFromHighlight} from './ViewerUtils';
import {InDocumentSearchProps} from './PdfViewer.types';
import {useStateDocumentHighlights} from '../../search/search-hooks';

interface TextViewerProps {
    title: string;
    content: string;
    onInDocumentSearchPropsChange: (inDocumentSearchProps: InDocumentSearchProps) => void;
}

export const TextViewer = memo(({title, content, onInDocumentSearchPropsChange}: TextViewerProps) => {
    const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
    const documentHighlights = useStateDocumentHighlights();
    const [annotationIds, setAnnotationIds] = useState<string[]>([]);
    const [contentWithEmTags, setContentWithEmTags] = useState<string>('');
    const [titleWithEmTags, setTitleWithEmTags] = useState<string>('');
    const refPreviewPageScroll: React.RefObject<any> = useRef(null);

    const onHighlightIndexChange = useCallback(
        (highlightIndex: number) => {
            setHighlightedIndex(highlightIndex);
            const htmlElement = document.getElementById(annotationIds[highlightIndex]);
            if (htmlElement) {
                scrollToElementFromHighlight(htmlElement, refPreviewPageScroll);
                highlightElement(htmlElement);
            }
        },
        [annotationIds],
    );

    useEffect(() => {
        setAnnotationIds(() => []);
        setContentWithEmTags(() => addIdsToEmTags(content, 'content'));
        setTitleWithEmTags(() => addIdsToEmTags(title, 'title'));
    }, [content, title]);

    useEffect(() => {
        const data: InDocumentSearchProps = {
            numHighlights: annotationIds.length,
            highlightedIndex: highlightedIndex,
            onHighlightIndexChange: onHighlightIndexChange,
        };
        onInDocumentSearchPropsChange(data);
    }, [annotationIds.length, highlightedIndex, onHighlightIndexChange, onInDocumentSearchPropsChange]);

    const addIdsToEmTags = (textWithEmTags: string, idClass: string) => {
        if (!textWithEmTags) {
            return '';
        }
        let index = 0;
        return textWithEmTags.replace(/(<em>)/g, () => {
            const highlightsId = 'hibu_' + idClass + '_hl_' + index++;
            setAnnotationIds((prev) => [...prev, highlightsId]);
            return '<em id="' + highlightsId + '">';
        });
    };

    // Only rewinds the counter. The new ids and markup land in the next render, so scrolling from here would
    // land on a hit of the old text.
    useEffect(() => {
        setHighlightedIndex(0);
    }, [title, content]);

    // Searching the same term twice returns identical text, so the effect above does not fire.
    // Keyed on the response instead. It only rewinds the counter: no scroll, no flash.
    useEffect(() => {
        setHighlightedIndex(0);
    }, [documentHighlights]);

    // Scrolls to the first hit once the new text's ids are in, and again when loading ends, because nothing is on
    // screen while loading.
    useEffect(() => {
        if (annotationIds.length > 0) {
            onHighlightIndexChange(0);
        }
        //eslint-disable-next-line
    }, [annotationIds]);

    return (
        <div id={'hibuTextPreview'}>
            <div id={'hibuTextPreviewPageWrapper'}>
                <div id={'hibuTextPreviewPageScroll'} ref={refPreviewPageScroll}>
                    <div id={'hibuTextPreviewPage'}>
                        <h1 id={'textViewerTitle'} dangerouslySetInnerHTML={{__html: titleWithEmTags}}/>
                        <pre id={'textViewerContent'} dangerouslySetInnerHTML={{__html: contentWithEmTags}}/>
                    </div>
                </div>
            </div>
        </div>
    );
});
